import { Box, CircularProgress, MuiIcons, Paper, Typography } from "@leap4/frontend";
import React from "react";
import { useGetExplorerRow } from "../../../../api/generated/api";
import type {
    ExplorerSubRowDto,
    GetExplorerRowBasis,
} from "../../../../api/generated/models";
import { DASH, formatCurrency, formatMetric, signColor } from "../format";

const { InfoOutlined } = MuiIcons;

interface IProps {
    campaign: string;
    basis: string;
    from: string;
    to: string;
    analyzeBy: string;
    splitBy: string | null;
    minImpressions: number | null;
    publishers: string[];
    secondaryValues: string[];
    secondaryLabel: string;
    dimValues: string[];
    onFilterPublisher: (publisher: string) => void;
}

/** Fetched on expand: these splits are a different grain from the table. */

const SubTable = ({
    title,
    rows,
    onClick,
}: {
    title: string;
    rows: ExplorerSubRowDto[];
    onClick?: (name: string) => void;
}): JSX.Element => (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <Box sx={{ px: 1.5, py: 0.75, borderBottom: 1, borderColor: "divider", bgcolor: "action.hover" }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {title}
            </Typography>
        </Box>
        <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
            {rows.length === 0 && (
                <Typography variant="caption" sx={{ display: "block", p: 1.5, color: "text.disabled" }}>
                    No data.
                </Typography>
            )}
            {rows.map((r) => (
                <Box
                    key={r.name}
                    onClick={onClick ? () => onClick(r.name) : undefined}
                    sx={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 1,
                        px: 1.5,
                        py: 0.5,
                        borderBottom: 1,
                        borderColor: "divider",
                        fontSize: 11,
                        cursor: onClick ? "pointer" : "default",
                        "&:hover": onClick ? { bgcolor: "action.hover" } : undefined,
                    }}
                    title={onClick ? `Filter the view to ${r.name}` : undefined}
                >
                    <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {r.name}
                    </Box>
                    <Box sx={{ width: 70, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        {formatCurrency(r.cost, true)}
                    </Box>
                    <Box sx={{ width: 60, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        {formatMetric(r.conversions, "count")}
                    </Box>
                    <Box
                        sx={{
                            width: 80,
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                            color: signColor(r.profit),
                        }}
                    >
                        {formatMetric(r.profit, "currency", true)}
                    </Box>
                </Box>
            ))}
        </Box>
    </Paper>
);

/** Profit by day. */
const Sparkline = ({
    points,
}: {
    points: { date: string; profit: number | null }[];
}): JSX.Element => {
    const values = points.map((p) => (p.profit !== null && Number.isFinite(p.profit) ? p.profit : 0));
    const max = Math.max(1, ...values.map(Math.abs));
    if (points.length === 0) {
        return (
            <Typography variant="caption" sx={{ color: "text.disabled" }}>
                No daily data.
            </Typography>
        );
    }
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: "1px", height: 48 }}>
            {points.map((p, i) => {
                const v = values[i];
                const h = Math.max(1, (Math.abs(v) / max) * 22);
                return (
                    <Box
                        key={p.date}
                        title={`${p.date}: ${formatMetric(p.profit, "currency", true)}`}
                        sx={{
                            flex: 1,
                            minWidth: 2,
                            height: 48,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                        }}
                    >
                        <Box sx={{ height: 24, display: "flex", alignItems: "flex-end" }}>
                            {v >= 0 && <Box sx={{ width: "100%", height: h, bgcolor: "success.main" }} />}
                        </Box>
                        <Box sx={{ height: 24, display: "flex", alignItems: "flex-start" }}>
                            {v < 0 && <Box sx={{ width: "100%", height: h, bgcolor: "error.main" }} />}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

const RowDetail = (props: IProps): JSX.Element => {
    const { secondaryLabel } = props;
    const { data, isLoading, isError } = useGetExplorerRow(
        {
            campaign: props.campaign,
            basis: props.basis as GetExplorerRowBasis,
            from: props.from,
            to: props.to,
            analyzeBy: props.analyzeBy,
            ...(props.splitBy ? { splitBy: props.splitBy } : {}),
            ...(props.minImpressions !== null ? { minImpressions: String(props.minImpressions) } : {}),
            ...(props.publishers.length > 0 ? { publisher: props.publishers } : {}),
            ...(props.secondaryValues.length > 0
                ? { secondaryDimension: "c3", secondaryValue: props.secondaryValues }
                : {}),
            value: props.dimValues,
        },
        { query: { staleTime: 120000, refetchOnWindowFocus: false } }
    );

    if (isLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={22} />
            </Box>
        );
    }

    if (isError || !data) {
        return (
            <Typography variant="caption" sx={{ color: "text.disabled", p: 2, display: "block" }}>
                Could not load this slice.
            </Typography>
        );
    }

    return (
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
            {data.narrative && (
                <Paper
                    variant="outlined"
                    sx={{ display: "flex", alignItems: "flex-start", gap: 1, px: 1.5, py: 1 }}
                >
                    <InfoOutlined sx={{ fontSize: 16, color: "text.disabled", mt: "1px" }} />
                    <Typography variant="body2">{data.narrative}</Typography>
                </Paper>
            )}

            <Box
                sx={{
                    display: "grid",
                    gap: 1.5,
                    gridTemplateColumns: { xs: "1fr", lg: "repeat(3, 1fr)" },
                }}
            >
                <SubTable
                    title="By publisher — cost · conv · profit"
                    rows={data.byPublisher}
                    onClick={props.onFilterPublisher}
                />
                {data.bySecondary && (
                    <SubTable title={`By ${secondaryLabel} — cost · conv · profit`} rows={data.bySecondary} />
                )}
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 1 }}>
                        Profit by day
                    </Typography>
                    <Sparkline points={data.daily} />
                </Paper>
            </Box>

            {data.context && (data.context.winners.length > 0 || data.context.losers.length > 0) && (
                <Box
                    sx={{
                        display: "grid",
                        gap: 1.5,
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        fontSize: 11,
                    }}
                >
                    <Typography variant="caption">
                        <Box component="span" sx={{ fontWeight: 600 }}>
                            Best combinations:{" "}
                        </Box>
                        {data.context.winners.slice(0, 4).map((s) => `${s.name} × ${data.label}`).join(" · ") ||
                            DASH}
                    </Typography>
                    <Typography variant="caption">
                        <Box component="span" sx={{ fontWeight: 600 }}>
                            Block combinations:{" "}
                        </Box>
                        {data.context.losers
                            .slice(0, 4)
                            .map((s) => `${s.name} × ${data.label} (${formatMetric(s.profit, "currency", true)})`)
                            .join(" · ") || DASH}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default RowDetail;
